import { z } from 'zod';
import { moneyStringSchema } from './common';
import { languageCodeSchema } from './courses';

/**
 * LMS Content Creator — registration / profile / admin lifecycle / cash-out
 * schemas. Mirrors `merchant.ts` minus the commerce-only parts (addresses,
 * inventory, variants); adds the public instructor profile fields.
 */

const creatorLinkSchema = z.object({
  label: z.string().min(1).max(60),
  url: z.string().url().max(500),
});
export type CreatorLinkInput = z.infer<typeof creatorLinkSchema>;

// ── Public self-registration (JSON; requirement files uploaded post-login) ──
export const creatorRegisterSchema = z.object({
  email: z.string().email().max(200),
  password: z.string().min(8).max(200),
  firstName: z.string().min(1).max(120),
  middleName: z.string().max(120).optional(),
  lastName: z.string().min(1).max(120),
  phone: z.string().min(1).max(40),
  /** Public instructor name (may differ from the account name). */
  displayName: z.string().min(1).max(120),
  bio: z.string().max(4000).nullable().optional(),
  links: z.array(creatorLinkSchema).max(10).optional(),
  requirements: z
    .array(z.object({ key: z.string().min(1).max(64), textValue: z.string().max(2000) }))
    .max(50)
    .optional(),
});
export type CreatorRegisterInput = z.infer<typeof creatorRegisterSchema>;

// ── Admin: create a creator outright (pre-approved, no documents to review) ──
export const adminCreatorCreateSchema = creatorRegisterSchema.omit({ requirements: true }).extend({
  platformFeePct: moneyStringSchema.optional(),
  showFeeToCreator: z.boolean().optional(),
  /** HOUSE account (tenant staff) — provisioned APPROVED, no platform fee. */
  isHouse: z.boolean().optional(),
});
export type AdminCreatorCreateInput = z.infer<typeof adminCreatorCreateSchema>;

export const adminCreatorUpdateSchema = z.object({
  platformFeePct: moneyStringSchema.optional(),
  showFeeToCreator: z.boolean().optional(),
  /** Settable on an APPROVED creator only (the route 409s otherwise). */
  isHouse: z.boolean().optional(),
});
export type AdminCreatorUpdateInput = z.infer<typeof adminCreatorUpdateSchema>;

/** Approve persists the commission in the same action — fee % REQUIRED (0 ok). */
export const adminCreatorApproveSchema = z.object({
  platformFeePct: moneyStringSchema,
  showFeeToCreator: z.boolean().optional(),
});
export type AdminCreatorApproveInput = z.infer<typeof adminCreatorApproveSchema>;

export const adminCreatorRejectSchema = z.object({
  reason: z.string().max(500).optional(),
});

export const adminCreatorRequirementReviewSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejectionReason: z.string().max(500).optional(),
});

// ── Creator self-service ───────────────────────────────────────────────────
export const creatorProfileUpdateSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  /** Owner display name — applied to the linked account's `users.full_name`. */
  ownerName: z.string().min(1).max(200).optional(),
  bio: z.string().max(4000).nullable().optional(),
  links: z.array(creatorLinkSchema).max(10).optional(),
});
export type CreatorProfileUpdateInput = z.infer<typeof creatorProfileUpdateSchema>;

export const creatorRequirementSubmitSchema = z.object({
  requirementKey: z.string().min(1).max(64),
  textValue: z.string().max(2000),
});

/**
 * Creator course create — the creator supplies the sellable bits AND the
 * curriculum meta in one call; the API creates the VIRTUAL product
 * (`is_virtual` forced, `approval_status='pending'`) + the course row in one
 * tx. Price is a money string.
 */
export const creatorCourseCreateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(5000).nullable().optional(),
  price: moneyStringSchema,
  /** Compare-at price shown struck through (optional). */
  srp: moneyStringSchema.nullable().optional(),
  /** Course topic — the course-category registry (NOT the product category). */
  categoryId: z.string().uuid().nullable().optional(),
  accessLevel: z.enum(['free', 'purchase', 'subscriber']).optional(),
  subtitle: z.string().max(300).nullable().optional(),
  learnPoints: z.array(z.string().min(1).max(300)).max(20).optional(),
  requirements: z.array(z.string().min(1).max(300)).max(20).optional(),
  level: z.enum(['beginner', 'intermediate', 'advanced']).nullable().optional(),
  /**
   * Content language ('en', 'fil', …). Display + catalog filter only; the
   * curated picker is `COURSE_LANGUAGES`, but ANY valid code is accepted so a
   * tenant is never boxed in by our list.
   */
  language: languageCodeSchema.nullable().optional(),
  /** Replace-set of course tags (the admin-managed registry). */
  tagIds: z.array(z.string().uuid()).max(10).optional(),
});
export type CreatorCourseCreateInput = z.infer<typeof creatorCourseCreateSchema>;

/**
 * Creator course update. A change to a SELLABLE field resets the product's
 * approval to `pending` (the merchant rule); curriculum-side edits don't.
 * The sellable set is `SELLABLE_KEYS` in `services/creator-courses.ts` — today
 * name / description / price / srp ONLY. `categoryId`, `language` and `tagIds`
 * are metadata: they never re-open review and never page an owner.
 */
export const creatorCourseUpdateSchema = creatorCourseCreateSchema.partial().extend({
  status: z.enum(['draft', 'published']).optional(),
});
export type CreatorCourseUpdateInput = z.infer<typeof creatorCourseUpdateSchema>;

export const creatorPayoutRequestSchema = z.object({
  amount: moneyStringSchema,
  method: z.string().min(1).max(64),
  payload: z.record(z.unknown()).optional(),
});
export type CreatorPayoutRequestInput = z.infer<typeof creatorPayoutRequestSchema>;

/** Admin: approve/reject a creator course's product listing. */
export const adminCreatorCourseReviewSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejectionReason: z.string().max(500).optional(),
  /**
   * Rule on the CONTENT in the same call.
   *
   * One editorial edit opens both states at once (the sellable reset queues the
   * listing, the tier-3 change row queues the content), and the review dialog
   * shows the reviewer one diff covering both. Making them press Approve twice
   * for one edit is the friction that gets a review setting switched off.
   *
   * Server-side rather than two client calls because the listing route does not
   * report `contentCleared` on the wire, a dropped second request would leave a
   * half-approved course, and hold-mode staging must be applied exactly once.
   */
  alsoContent: z.boolean().optional(),
});

/**
 * Buyer places a CREATOR course order — one creator's course products (mirror
 * of `merchantOrderCreateSchema`, minus shipping: courses are virtual). Shares
 * `checkoutGroupId` with the house order + sibling sub-orders so the buyer
 * pays once through the existing group-payment flow.
 */
export const creatorOrderCreateSchema = z.object({
  creatorUserId: z.string().uuid(),
  items: z
    .array(
      z.object({
        productId: z.string().uuid(),
        qty: z.number().int().positive().max(100),
        // A subscription plan line to renew automatically (cash wallet only).
        autoRenew: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(50),
  // Manual or cash-wallet method chosen at checkout; the server validates it
  // against the enabled ecom registry. Omitted = first enabled manual method.
  paymentMethodCode: z.string().max(64).optional(),
  // Which of the buyer's slots pays (wallet balances are per-slot). Ignored
  // unless it's one of their own active slots; falls back to their earliest.
  buyerSlotId: z.string().uuid().optional(),
  checkoutGroupId: z.string().uuid().optional(),
  notes: z.string().max(1000).optional(),
  // A CREATOR-owned coupon code (this creator's own). House and merchant codes
  // are rejected by the owner scope in `resolveCoupon`, so a buyer can never
  // spend a house discount on a third-party creator's course.
  couponCode: z.string().trim().min(1).max(64).optional(),
});
export type CreatorOrderCreateInput = z.infer<typeof creatorOrderCreateSchema>;

// ── Creator subscription plans (LMS Phase 10) ───────────────────────────────

/**
 * The three billing periods a creator may offer. Stored as TEXT with a CHECK
 * rather than a pg enum: adding an enum value and using it in the same
 * transaction is what the one-tx migrator refuses, and a fourth period is a
 * plausible future ask.
 *
 * `semi_annual` is 6 months and `annual` is 12 — the interval arithmetic lives
 * in SQL (`services/creator-subscriptions.ts`), never in JS date maths, so a
 * period that starts on the 31st lands where postgres says it does.
 */
export const CREATOR_SUBSCRIPTION_PERIODS = ['monthly', 'semi_annual', 'annual'] as const;
export type CreatorSubscriptionPeriod = (typeof CREATOR_SUBSCRIPTION_PERIODS)[number];

/** Months per period — the ONE place the mapping lives. */
export const CREATOR_SUBSCRIPTION_MONTHS: Record<CreatorSubscriptionPeriod, number> = {
  monthly: 1,
  semi_annual: 6,
  annual: 12,
};

export const CREATOR_SUBSCRIPTION_PERIOD_LABELS: Record<CreatorSubscriptionPeriod, string> = {
  monthly: 'Monthly',
  semi_annual: '6 months',
  annual: '12 months',
};

/**
 * The creator's plan editor: the WHOLE set in one PUT, not a delta.
 *
 * A period the creator omits is turned OFF rather than left alone — the form
 * always sends all three, and "absent means unchanged" would make it
 * impossible to withdraw a plan by unticking it.
 */
export const creatorSubscriptionPlansUpdateSchema = z.object({
  plans: z
    .array(
      z.object({
        period: z.enum(CREATOR_SUBSCRIPTION_PERIODS),
        active: z.boolean(),
        // Positive, not just non-negative: a ₱0 subscription is a free course
        // with extra steps, and it would sell through checkout for nothing.
        price: moneyStringSchema.regex(
          /^\d+(\.\d{1,4})?$/,
          'must be a non-negative decimal with up to 4 fractional digits',
        ),
      }),
    )
    .max(CREATOR_SUBSCRIPTION_PERIODS.length),
});
export type CreatorSubscriptionPlansUpdateInput = z.infer<
  typeof creatorSubscriptionPlansUpdateSchema
>;

/** Admin decision on a third-party creator's plan product. */
export const adminCreatorPlanReviewSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  reason: z.string().trim().max(500).optional(),
});
export type AdminCreatorPlanReviewInput = z.infer<typeof adminCreatorPlanReviewSchema>;
